import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  UserPlus,
  UserMinus,
  ExternalLink,
  X,
  ChevronDown,
  ChevronRight,
  Users,
  Info,
} from 'lucide-react';
import { useProjet, useDesactiverProjet, useAffecterEmploye, useRetirerEmploye } from '../hooks/useProjets';
import { useEmployes } from '../../employes/hooks/useEmployes';
import type { AffectationAvecEmploye } from '../types';
import { useContributionsParProjet } from '../../contributionsProjet/presentation/hooks/useContributionsProjet';

/** Génère une couleur Tailwind déterministe depuis le nom — intentionnel, palette de marque */
function couleurAvatar(nom: string): string {
  const palette = [
    'bg-indigo-500', 'bg-violet-500', 'bg-blue-500', 'bg-sky-500',
    'bg-emerald-500', 'bg-teal-500', 'bg-amber-500', 'bg-rose-500',
  ];
  let hash = 0;
  for (let i = 0; i < nom.length; i++) {
    hash = nom.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function formaterDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ProjetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: projet, isLoading, isError, error } = useProjet(id);
  const desactiverProjet = useDesactiverProjet();
  const affecterEmploye = useAffecterEmploye(id || '');
  const retirerEmploye = useRetirerEmploye(id || '');

  const [panelAffectation, setPanelAffectation] = useState(false);
  const [employeSelectionne, setEmployeSelectionne] = useState('');
  const [dateAffectation, setDateAffectation] = useState('');
  const [sectionTermineesOuverte, setSectionTermineesOuverte] = useState(false);
  const confirm = useConfirm();

  const { data: employesActifs } = useEmployes({ actif: true });

  const { actives, terminees } = useMemo(() => {
    if (!projet?.affectations) return { actives: [], terminees: [] };
    return {
      actives: projet.affectations.filter((a: AffectationAvecEmploye) => a.actif && !a.dateFin),
      terminees: projet.affectations.filter((a: AffectationAvecEmploye) => !a.actif || !!a.dateFin),
    };
  }, [projet]);

  const handleDesactiver = async () => {
    if (!projet) return;
    const ok = await confirm({
      title: `Désactiver le projet "${projet.nom}" ?`,
      message: `Le projet ${projet.code} sera marqué comme inactif.`,
      variant: 'warning',
      labelConfirm: 'Désactiver',
    });
    if (!ok) return;
    await desactiverProjet.mutateAsync(projet.idProjet);
    navigate('/paie/administration/projets');
  };

  const handleAffecter = async () => {
    if (!employeSelectionne || !dateAffectation) return;
    await affecterEmploye.mutateAsync({
      idEmploye: employeSelectionne,
      dateDebut: dateAffectation,
    });
    setEmployeSelectionne('');
    setDateAffectation('');
    setPanelAffectation(false);
  };

  const handleRetirer = async (aff: AffectationAvecEmploye) => {
    const nomComplet = aff.employe
      ? `${aff.employe.nom} ${aff.employe.prenoms}`
      : aff.nomEmploye || aff.idEmploye;
    const ok = await confirm({
      title: `Retirer ${nomComplet} du projet ?`,
      message: 'L\'affectation sera terminée. L\'employé restera dans la liste générale.',
      variant: 'warning',
      labelConfirm: 'Retirer',
    });
    if (!ok) return;
    await retirerEmploye.mutateAsync(aff.idEmploye);
  };

  // ── Chargement ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="h-8 w-32 bg-surface-overlay rounded animate-pulse" />
        <div className="bg-surface border border-border rounded-2xl p-6 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-surface-overlay rounded-2xl" />
            <div className="flex-1 space-y-3">
              <div className="h-5 bg-surface-overlay rounded w-1/3" />
              <div className="h-4 bg-surface-raised rounded w-1/2" />
            </div>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-2xl p-6 animate-pulse">
          <div className="grid grid-cols-2 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-3 bg-surface-raised rounded w-20" />
                <div className="h-4 bg-surface-overlay rounded w-32" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-surface border border-border rounded-2xl p-6 animate-pulse">
          <div className="h-5 bg-surface-overlay rounded w-48 mb-4" />
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 py-3">
              <div className="w-9 h-9 bg-surface-overlay rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-surface-overlay rounded w-40" />
                <div className="h-3 bg-surface-raised rounded w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Erreur ──────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-danger-light border border-danger rounded-2xl p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 bg-danger-light rounded-2xl flex items-center justify-center">
            <Info className="w-7 h-7 text-danger" />
          </div>
          <h2 className="text-lg font-semibold text-danger mb-2">Erreur de chargement</h2>
          <p className="text-sm text-danger mb-6 opacity-80">
            {error instanceof Error ? error.message : 'Impossible de charger le projet.'}
          </p>
          <button
            onClick={() => navigate('/paie/administration/projets')}
            className="inline-flex items-center gap-2 text-sm font-medium text-danger
                       hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la liste des projets
          </button>
        </div>
      </div>
    );
  }

  // ── Projet introuvable ──────────────────────────────────────
  if (!projet) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-surface border border-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-surface-raised rounded-2xl flex items-center justify-center">
            <Briefcase className="w-8 h-8 text-content-subtle" />
          </div>
          <h2 className="text-lg font-semibold text-content-muted mb-2">Projet introuvable</h2>
          <p className="text-sm text-content-subtle mb-6">Ce projet n'existe pas ou a été supprimé.</p>
          <Link
            to="/paie/administration/projets"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-text
                       hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à la liste des projets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* ── Bouton retour ────────────────────────────────────── */}
      <button
        onClick={() => navigate('/paie/administration/projets')}
        className="inline-flex items-center gap-1.5 text-sm text-content-subtle hover:text-content
                   transition-colors mb-6 group"
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        Retour aux projets
      </button>

      {/* ── En-tête projet ───────────────────────────────────── */}
      <div className="bg-surface border border-border rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-primary-light flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-7 h-7 text-primary-text" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-sm font-mono font-semibold text-primary-text bg-primary-light px-2.5 py-0.5 rounded-md">
                  {projet.code}
                </span>
                {projet.actif ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                                 bg-success-light text-success border border-success">
                    Actif
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold
                                 bg-surface-overlay text-content-subtle border border-border">
                    Inactif
                  </span>
                )}
              </div>
              <h1 className="text-xl font-bold text-content tracking-tight">{projet.nom}</h1>
              {projet.organisation && (
                <div className="flex items-center gap-1.5 mt-1">
                  <Building2 className="w-3.5 h-3.5 text-content-subtle" />
                  <span className="text-sm text-content-subtle">{projet.organisation.nom}</span>
                </div>
              )}
              {projet.description && (
                <p className="text-sm text-content-subtle mt-1.5 max-w-lg">{projet.description}</p>
              )}
            </div>
          </div>

          {projet.actif && (
            <button
              onClick={handleDesactiver}
              disabled={desactiverProjet.isPending}
              className="text-sm font-medium text-danger hover:bg-danger-light
                         px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              Désactiver
            </button>
          )}
        </div>
      </div>

      {/* ── Informations du projet ───────────────────────────── */}
      <div className="bg-surface border border-border rounded-2xl shadow-sm mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-border bg-surface-raised">
          <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wider">
            Informations du projet
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-1">
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              Date de début
            </p>
            <p className="text-sm font-medium text-content">{formaterDate(projet.dateDebut)}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-1">
              <Calendar className="w-3.5 h-3.5 inline mr-1" />
              Date de fin
            </p>
            <p className="text-sm font-medium text-content">
              {projet.dateFin ? formaterDate(projet.dateFin) : (
                <span className="italic text-content-subtle">En cours</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-1">Code projet</p>
            <p className="text-sm font-mono font-semibold text-content">{projet.code}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-1">Statut</p>
            <p className="text-sm font-medium">
              {projet.actif ? (
                <span className="text-success">Actif</span>
              ) : (
                <span className="text-content-subtle">Inactif</span>
              )}
            </p>
          </div>
          {projet.organisation && (
            <div className="sm:col-span-2">
              <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-1">
                <Building2 className="w-3.5 h-3.5 inline mr-1" />
                Organisation
              </p>
              <p className="text-sm font-medium text-content">{projet.organisation.nom}</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Employés affectés ────────────────────────────────── */}
      <div className="bg-surface border border-border rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-border bg-surface-raised flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wider">
              Employés affectés
            </h2>
            <span className="text-xs font-semibold bg-primary-light text-primary-text px-2 py-0.5 rounded-full">
              {actives.length}
            </span>
          </div>
          {projet.actif && (
            <button
              onClick={() => setPanelAffectation(!panelAffectation)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-text
                         hover:text-primary hover:bg-primary-light px-3 py-1.5 rounded-lg transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Affecter un employé
            </button>
          )}
        </div>

        {/* Panel d'affectation */}
        {panelAffectation && (
          <div className="px-6 py-5 border-b border-border bg-primary-light/30">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-content-muted mb-1.5">
                  <Users className="w-3.5 h-3.5 inline mr-1 text-content-subtle" />
                  Employé
                </label>
                <select
                  value={employeSelectionne}
                  onChange={(e) => setEmployeSelectionne(e.target.value)}
                  className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-surface text-content
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                             transition-shadow"
                >
                  <option value="">-- Sélectionner un employé --</option>
                  {employesActifs?.items?.map((emp) => (
                    <option key={emp.idEmploye} value={emp.idEmploye}>
                      {emp.nom} {emp.prenoms} — {emp.fonction}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-full sm:w-48">
                <label className="block text-sm font-medium text-content-muted mb-1.5">
                  <Calendar className="w-3.5 h-3.5 inline mr-1 text-content-subtle" />
                  Date début
                </label>
                <input
                  type="date"
                  value={dateAffectation}
                  onChange={(e) => setDateAffectation(e.target.value)}
                  className="w-full border border-border rounded-lg px-3.5 py-2.5 text-sm bg-surface text-content
                             focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                             transition-shadow"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAffecter}
                  disabled={!employeSelectionne || !dateAffectation || affecterEmploye.isPending}
                  className="px-4 py-2.5 text-sm font-semibold text-content-inverted bg-primary
                             hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed
                             rounded-lg transition-all shadow-sm"
                >
                  {affecterEmploye.isPending ? 'Affectation...' : 'Affecter'}
                </button>
                <button
                  onClick={() => { setPanelAffectation(false); setEmployeSelectionne(''); setDateAffectation(''); }}
                  className="p-2.5 text-content-subtle hover:text-content-muted hover:bg-surface-overlay
                             rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {affecterEmploye.isError && (
              <p className="text-sm text-danger mt-3 bg-danger-light border border-danger rounded-lg p-2.5">
                Erreur lors de l'affectation. Veuillez réessayer.
              </p>
            )}
          </div>
        )}

        {/* Tableau des affectations actives */}
        {actives.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr className="bg-surface-raised">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Employé
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Fonction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Depuis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {actives.map((aff: AffectationAvecEmploye) => {
                  const nom = aff.employe ? `${aff.employe.nom} ${aff.employe.prenoms}` : (aff.nomEmploye || '—');
                  const initiales = aff.employe
                    ? `${aff.employe.nom[0]}${aff.employe.prenoms[0]}`
                    : (aff.nomEmploye?.[0] || '?');
                  return (
                    <tr key={aff.idAffectation} className="hover:bg-surface-raised transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg ${couleurAvatar(nom)} flex items-center justify-center
                                         text-white text-xs font-bold flex-shrink-0`}>
                            {initiales}
                          </div>
                          <span className="text-sm font-medium text-content">{nom}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-content-muted">
                        {aff.employe?.fonction || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-content-muted">
                        {formaterDate(aff.dateDebut)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {aff.employe && (
                            <Link
                              to={`/paie/employes/${aff.employe.idEmploye}`}
                              className="text-xs font-medium text-primary-text hover:text-primary
                                         hover:bg-primary-light px-2.5 py-1.5 rounded-lg transition-colors
                                         inline-flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              Voir la fiche
                            </Link>
                          )}
                          {projet.actif && (
                            <button
                              onClick={() => handleRetirer(aff)}
                              disabled={retirerEmploye.isPending}
                              className="text-xs font-medium text-danger hover:bg-danger-light
                                         px-2.5 py-1.5 rounded-lg transition-colors
                                         inline-flex items-center gap-1 disabled:opacity-50"
                            >
                              <UserMinus className="w-3 h-3" />
                              Retirer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-10 text-center">
            <Users className="w-10 h-10 text-content-subtle mx-auto mb-3" />
            <p className="text-sm text-content-subtle">Aucun employé affecté à ce projet.</p>
            {projet.actif && (
              <button
                onClick={() => setPanelAffectation(true)}
                className="mt-3 text-sm font-medium text-primary-text hover:text-primary transition-colors"
              >
                Affecter un premier employé
              </button>
            )}
          </div>
        )}

        {/* Affectations terminées (section repliable) */}
        {terminees.length > 0 && (
          <div className="border-t border-border">
            <button
              onClick={() => setSectionTermineesOuverte(!sectionTermineesOuverte)}
              className="w-full px-6 py-3 flex items-center justify-between text-sm text-content-subtle
                         hover:bg-surface-raised transition-colors"
            >
              <span className="font-medium">
                Affectations terminées ({terminees.length})
              </span>
              {sectionTermineesOuverte ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
            {sectionTermineesOuverte && (
              <div className="px-6 pb-4 space-y-2">
                {terminees.map((aff: AffectationAvecEmploye) => {
                  const nom = aff.employe
                    ? `${aff.employe.nom} ${aff.employe.prenoms}`
                    : (aff.nomEmploye || '—');
                  return (
                    <div
                      key={aff.idAffectation}
                      className="flex items-center justify-between py-2 px-3 bg-surface-raised rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-content-muted">{nom}</span>
                        {aff.employe?.fonction && (
                          <span className="text-xs text-content-subtle">— {aff.employe.fonction}</span>
                        )}
                      </div>
                      <div className="text-xs text-content-subtle">
                        {formaterDate(aff.dateDebut)}
                        {aff.dateFin && ` → ${formaterDate(aff.dateFin)}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bloc contributions */}
      <ContributionsProjetSection idProjet={id} />
    </div>
  );
}

function ContributionsProjetSection({ idProjet }: { idProjet: string | undefined }) {
  const { data: contributions = [] } = useContributionsParProjet(idProjet);
  const f = (n: number) => n.toLocaleString('fr-FR') + ' F';
  return (
    <div className="bg-surface border border-border rounded-2xl overflow-hidden shadow-sm mt-6">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Employés en contribution</h2>
        <Link
          to={`/paie/contributions/projet/${idProjet}`}
          className="text-xs text-primary-text hover:text-primary font-medium"
        >
          Gérer →
        </Link>
      </div>
      {contributions.length === 0 ? (
        <div className="px-6 py-4 text-sm text-slate-400">Aucune contribution enregistrée</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {contributions.map(c => (
            <div key={c.idContribution} className="px-6 py-3 flex items-center justify-between">
              <Link to={`/paie/employes/${c.idEmploye}`} className="text-sm text-content hover:text-primary">
                {c.nomEmploye}
              </Link>
              <span className="text-sm font-semibold font-mono tabular-nums text-primary-text">{f(c.montant)}</span>
            </div>
          ))}
          <div className="px-6 py-3 flex items-center justify-between bg-slate-50">
            <span className="text-xs font-semibold text-slate-500 uppercase">Total</span>
            <span className="text-sm font-bold font-mono tabular-nums text-content">{f(contributions.reduce((s, c) => s + c.montant, 0))}</span>
          </div>
        </div>
      )}
    </div>
  );
}
